import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useThemeMode } from "../../ThemeContext";
import { dataAPI } from "../../helpers/api_service";
import { APP_URLS } from "../../helpers/url_helper";

function CompanyFormRecords() {
  const { mode } = useThemeMode();
  const navigate = useNavigate();
  const location = useLocation();
  const form = location.state?.form || null;
  const [columns, setColumns] = useState([]);
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    const loadRecords = async () => {
      const formId = form?.id;
      if (!formId) {
        setIsLoading(false);
        setErrorMessage("Form id is missing. Please open records from list page.");
        return;
      }

      try {
        setIsLoading(true);
        setErrorMessage("");
        const response = await dataAPI.getDigitalFormClientFormSubmissions(formId);
        const data = response?.data || {};
        const apiColumns = Array.isArray(data.columns) ? data.columns : [];
        const apiRows = Array.isArray(data.rows) ? data.rows : [];
        setColumns(apiColumns);
        setRows(apiRows);
      } catch (error) {
        setColumns([]);
        setRows([]);
        setErrorMessage(error?.message || "Failed to load form records.");
      } finally {
        setIsLoading(false);
      }
    };

    loadRecords();
  }, [form]);

  const mappedRows = useMemo(() => {
    return rows.map((row) => {
      const answers = Array.isArray(row?.answers) ? row.answers : [];
      const answerMap = {};
      answers.forEach((answer) => {
        const label = String(answer?.label || "").trim();
        if (label) {
          answerMap[label] = answer?.value ?? "";
        }
      });
      return {
        id: row?.id,
        values: columns.map((column) => answerMap[column?.label] ?? ""),
      };
    });
  }, [rows, columns]);

  return (
    <div style={{ padding: 20 }}>
      <div style={wrapperStyle(mode)}>
        <button
          type="button"
          onClick={() => navigate(APP_URLS.COMPANY_FORMS)}
          style={backButtonStyle(mode)}
        >
          <ArrowBackIcon style={{ fontSize: 18 }} />
          Back to Company Forms
        </button>

        <h2 style={titleStyle(mode)}>{form?.title || "Company Form Records"}</h2>
        <p style={descriptionStyle(mode)}>{form?.description || "No description"}</p>

        {!isLoading && errorMessage && (
          <div style={errorStyle(mode)}>
            {errorMessage}
          </div>
        )}

        {!isLoading && !errorMessage && columns.length === 0 && (
          <div style={infoStyle(mode)}>
            No records found.
          </div>
        )}

        {!isLoading && !errorMessage && columns.length > 0 && (
          <div style={tableWrapperStyle(mode)}>
            <table style={tableStyle(columns.length)}>
              <thead>
                <tr>
                  {columns.map((column) => (
                    <th key={column.id || column.label} style={headCellStyle(mode)}>
                      {column.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {mappedRows.map((row) => (
                  <tr key={row.id}>
                    {row.values.map((value, index) => (
                      <td key={`${row.id}-${columns[index]?.id || index}`} style={bodyCellStyle(mode)}>
                        {Array.isArray(value) ? value.join(", ") : String(value || "-")}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

const wrapperStyle = (mode) => ({
  border: "none",
  borderRadius: 12,
  background: mode === "dark" ? "#121212" : "#fff",
  padding: 16,
});

const backButtonStyle = (mode) => ({
  border: `1px solid ${mode === "dark" ? "#4a4a4a" : "#d0d5dd"}`,
  background: "transparent",
  color: mode === "dark" ? "#fff" : "#111",
  borderRadius: 8,
  padding: "7px 12px",
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  marginBottom: 12,
});

const titleStyle = (mode) => ({
  margin: "0 0 6px",
  color: mode === "dark" ? "#fff" : "#0f172a",
});

const descriptionStyle = (mode) => ({
  margin: "0 0 14px",
  color: mode === "dark" ? "#98a2b3" : "#64748b",
  fontSize: 13,
});

const infoStyle = (mode) => ({
  border: `1px dashed ${mode === "dark" ? "#475467" : "#cbd5e1"}`,
  borderRadius: 10,
  padding: 14,
  color: mode === "dark" ? "#cbd5e1" : "#475569",
  fontSize: 14,
});

const errorStyle = (mode) => ({
  border: `1px solid ${mode === "dark" ? "#704040" : "#f1b2b2"}`,
  background: mode === "dark" ? "#2b1717" : "#fff3f3",
  color: mode === "dark" ? "#ffb0b0" : "#a53030",
  borderRadius: 8,
  padding: "12px 14px",
  fontSize: 14,
});

const tableWrapperStyle = (mode) => ({
  border: `1px solid ${mode === "dark" ? "#344054" : "#e2e8f0"}`,
  borderRadius: 10,
  overflowX: "auto",
});

const tableStyle = (columnsCount) => ({
  width: "100%",
  minWidth: Math.max(680, columnsCount * 180),
  borderCollapse: "collapse",
});

const headCellStyle = (mode) => ({
  textAlign: "left",
  padding: "11px 12px",
  borderBottom: `1px solid ${mode === "dark" ? "#344054" : "#e2e8f0"}`,
  background: mode === "dark" ? "#171717" : "#f8fafc",
  color: mode === "dark" ? "#e4e7ec" : "#334155",
  fontSize: 13,
  fontWeight: 600,
});

const bodyCellStyle = (mode) => ({
  padding: "11px 12px",
  color: mode === "dark" ? "#d0d5dd" : "#0f172a",
  borderBottom: `1px solid ${mode === "dark" ? "#2f2f2f" : "#f1f5f9"}`,
  fontSize: 13,
});

export default CompanyFormRecords;
